<div class="ai-assist" *ngIf="requestId">

  <div class="flex align-items-center justify-content-center p-4" *ngIf="loading">
    <p-progressSpinner styleClass="w-3rem h-3rem"></p-progressSpinner>
  </div>

  <div class="p-error mb-3" *ngIf="loadError">{{ loadError }}</div>

  <div *ngIf="request && !loading">

    <!-- ── Block 1 — doc-type buttons, first row, same as the standard "Новый документ"
         tab: one click seeds newDocument, active type goes green. The LLM's guess is
         applied automatically on load, so the blocks below open already filled. ── -->
    <p-button label="Выставить КП" class="col mr-2" (onClick)="applyDocType(12)"
              [severity]="(appliedDocType===12)?'success':'primary'"
              pTooltip="Создать новое коммерческое предложение по этой заявке" tooltipPosition="top"
              *ngIf="!isKeyAccount"></p-button>
    <p-button label="Выставить счет" class="col mr-2" (onClick)="applyDocType(1)"
              [severity]="(appliedDocType===1)?'success':'primary'"
              pTooltip="Создать новый счет по этой заявке" tooltipPosition="top"
              *ngIf="!isKeyAccount"></p-button>
    <small class="text-500 ml-2" *ngIf="!isKeyAccount">
      AI предлагает: {{ suggestedDocType === 'invoice' ? 'счет' : 'коммерческое предложение' }}
    </small>

    <!-- Key account -> COPILOT MODE: no document is auto-seeded, the manager starts it by
         hand on the standard "Комерційна пропозиція"/"Рахунок" tab of this same dialog. The
         AI's findings -- including, since 2026-09-15, the resolved product candidates below
         -- still show and are still usable once that document exists; only the one-click
         auto-seed is blocked, never the AI's help itself. -->
    <p-tag *ngIf="isKeyAccount" severity="success" value="COPILOT MODE" styleClass="mb-3 text-lg"></p-tag>
    <small class="text-500 block mb-3" *ngIf="isKeyAccount && !newDocument">
      Документ для ключевого клиента не создаётся автоматически — начните его на вкладке
      «Коммерческое предложение» или «Счет», затем вернитесь сюда, чтобы принять товары из письма.
    </small>

    <!-- ── Blocks 2-8 — ONE accordion, same p-fieldset > p-accordion composition as the
         standard flow, with the AI's findings interleaved between the standard blocks
         rather than stacked above them. The standard blocks are the same components the
         host dialog renders, with the same bindings; keep both sides in step (see this
         component's class comment). ── -->
    <div class="grid mt-3">
      <p-fieldset class="col-12">
        <ng-template pTemplate="header">
          <h3>{{ docHeader || 'Заявка из почты' }}</h3>
        </ng-template>

        <p-accordion [multiple]="true">

          <!-- AI: the source email itself -->
          <p-accordionTab [selected]="true">
            <ng-template pTemplate="header"><h5>Заявка из почты</h5></ng-template>
            <div class="fluid grid" appStopAccordionHomeEnd>
              <div class="col-6">
                <label class="block font-bold">Отправитель</label>
                <div>{{ request.sender_email }}</div>
              </div>
              <div class="col-6">
                <label class="block font-bold">Получено</label>
                <div>{{ request.received_at | date:'short' }}</div>
              </div>
              <div class="col-12">
                <label class="block font-bold">Текст письма</label>
                <textarea pInputTextarea [ngModel]="readableEmailText" readonly rows="6" class="w-full"></textarea>
              </div>
            </div>
          </p-accordionTab>

          <!-- Block 2 — dates, currency, our company -->
          <p-accordionTab [selected]="true" *ngIf="newDocument">
            <ng-template pTemplate="header"><h5>Дата, валюта, продавец</h5></ng-template>
            <app-new-document-dlg-dates appStopAccordionHomeEnd
              [newDocument]="newDocument"
              [parentDocument]="parentDocument"
              (newDocumentUpdate)="newDocumentUpdate.emit($event)">
            </app-new-document-dlg-dates>
          </p-accordionTab>

          <!-- AI: what the agent found about the customer -- context for confirming block
               3b, deliberately not a second selector for the same fields. -->
          <p-accordionTab [selected]="true">
            <ng-template pTemplate="header"><h5>Что нашёл AI по клиенту</h5></ng-template>
            <div class="grid" appStopAccordionHomeEnd>
              <div class="col-12" *ngIf="!request.customer_id">
                <p-tag severity="warning" value="Клиент не определен автоматически"></p-tag>
              </div>

              <!-- The resolver's own account of what it matched, or of what each step saw
                   when it matched nothing. Shown verbatim: when no customer was found this
                   is the only evidence of whether the sender is genuinely unknown or a
                   contact that does exist was lost on the way. -->
              <div class="col-12" *ngIf="customerResolution as cr">
                <label class="block font-bold">Как AI искал клиента</label>
                <p-tag [severity]="cr.confidence === 'HIGH' ? 'success' : cr.confidence === 'MEDIUM' ? 'info' : 'warning'"
                       [value]="cr.confidence" styleClass="mr-2"></p-tag>
                <span class="text-500">{{ cr.reason }}</span>
                <div class="mt-1" *ngIf="cr.personId; else noContact">
                  <span class="text-500">Контакт определён по адресу отправителя (Person.PERSID {{ cr.personId }}) —
                    подставлен в «Результат поиска контрагента» ниже, проверьте.</span>
                </div>
                <ng-template #noContact>
                  <div class="mt-1 text-500" *ngIf="cr.customerId">
                    Конкретный контакт не определён — компания найдена не по адресу отправителя.
                    Выберите ответственного ниже.
                  </div>
                </ng-template>
              </div>
              <ng-container *ngIf="customer as c">
                <div class="col-6">
                  <label class="block font-bold">Клиент</label>
                  <div>{{ c.customer.CLIENTSNAME || c.customer.CLIENTSNIKNAME }} (CLIID {{ c.customer.CLIID }})</div>
                </div>
                <div class="col-6">
                  <label class="block font-bold">Уровень клиента</label>
                  <p-tag *ngIf="c.tier as t; else noTier" [severity]="isKeyAccount ? 'success' : 'info'" [value]="t.tier"></p-tag>
                  <ng-template #noTier><span class="text-500">не задан</span></ng-template>
                </div>
                <div class="col-12" *ngIf="c.customer">
                  <label class="block font-bold">Последние заказы</label>
                  <app-document-list-table [clientId]="c.customer.CLIID"></app-document-list-table>
                </div>
              </ng-container>
              <div class="col-6">
                <label class="block font-bold">Наша компания (по домену)</label>
                <div *ngIf="request.resolved_company_id; else noCompany">
                  CLIID {{ request.resolved_company_id }} ({{ request.company_confidence }})
                </div>
                <ng-template #noCompany><span class="text-500">не определена</span></ng-template>
              </div>
            </div>
            <small class="text-500 block mt-2">
              Это подсказка AI. Покупатель и юридическое лицо покупателя подтверждаются ниже, в блоке
              «Результат поиска контрагента».
            </small>
          </p-accordionTab>

          <!-- Block 3a — free-form counterparty search; feeds block 3b. Sits *after* the
               AI block on purpose: what the agent found is the context you read before
               deciding whether a manual search is needed at all, and on the agent path it
               is usually the answer. Collapsed once a buyer is set -- see
               openClientSearchBlock. -->
          <p-accordionTab [selected]="openClientSearchBlock" *ngIf="newDocument">
            <ng-template pTemplate="header"><h5>Поиск контрагента</h5></ng-template>
            <app-company-search appStopAccordionHomeEnd
              header="Поиск организации-контрагента"
              (selectedIds)="selectedIds.emit($event)">
            </app-company-search>
          </p-accordionTab>

          <!-- Block 3b — counterparty result: buyer + buyer's PCO, each with responsible
               person. Pre-filled from the AI's guess, still explicitly confirmed here. -->
          <p-accordionTab [selected]="openCounterpartyBlock" *ngIf="newDocument">
            <ng-template pTemplate="header"><h5>Результат поиска контрагента {{ resultPartnerName }}</h5></ng-template>
            <app-new-document-dlg-companies appStopAccordionHomeEnd
              [newDocument]="newDocument"
              [parentDocument]="parentDocument"
              (newDocumentUpdate)="newDocumentUpdate.emit($event)">
            </app-new-document-dlg-companies>

            <!-- AG-21b: create a new Clients/Person row (or add a contact to the
                 already-selected company) straight from what the agent extracted
                 from the email signature -- only shown when there is something
                 useful to act on (quickCreateMode is null otherwise). -->
            <app-agent-customer-quick-create appStopAccordionHomeEnd
              *ngIf="quickCreateMode as mode"
              [mode]="mode"
              [extractedSignals]="customerResolution?.extractedSignals ?? null"
              [senderEmail]="request.sender_email"
              [cliId]="newDocument.DOCCLIENTSID"
              (created)="onQuickCreateResult($event)">
            </app-agent-customer-quick-create>

            <!-- AG-22: offer to update stale customer/contact info from extracted email
                 signals when a customer was already matched but the email's signature block
                 names different company/phone/contact details. Only shown on Step 3 matches
                 (customer_id set AND extractedSignals non-null). Mutually exclusive with
                 quickCreateMode by construction: quick-create needs customer_id=null,
                 update-offer needs customer_id set. -->
            <app-agent-customer-update-offer appStopAccordionHomeEnd
              *ngIf="showUpdateOffer"
              [customerId]="request.customer_id"
              [personId]="customerResolution?.personId ?? null"
              [extractedSignals]="customerResolution?.extractedSignals ?? null">
            </app-agent-customer-update-offer>
          </p-accordionTab>

          <!-- Block 4 — delivery conditions. AI-filled content here is a later cycle. -->
          <p-accordionTab [selected]="true" *ngIf="newDocument">
            <ng-template pTemplate="header"><h5>Условия поставки</h5></ng-template>
            <app-edit-document-conditions appStopAccordionHomeEnd [document]="newDocument"></app-edit-document-conditions>
          </p-accordionTab>

          <!-- AI: extracted products. Sits last, where the real product panel lands next
               cycle -- the standard dialog has none for a brand-new document today.
               Shown for COPILOT-mode (isKeyAccount) customers too, since 2026-09-15 -- the
               AI's catalog matching is exactly the routine work this panel exists to save,
               and hiding it entirely left a key account's manager with none of that help.
               Only the auto-seed stayed blocked; accepting into an existing document still
               requires the manager's own click, same as any other customer. Radio buttons
               disabled with an explanatory note above until a document exists (see
               isKeyAccount && !newDocument), so a click never silently does nothing. -->
          <p-accordionTab [selected]="true" *ngIf="resolvedLines.length">
            <ng-template pTemplate="header">
              <h5 class="flex align-items-center gap-2">
                <span>Товары из письма</span>
                <ng-container *ngIf="resolvingProducts">
                  <i class="pi pi-spin pi-spinner text-500"></i>
                  <span class="text-500 text-sm font-normal">
                    поиск в каталоге... ({{ resolvedLinesDone }} из {{ resolvedLines.length }})
                  </span>
                </ng-container>
              </h5>
            </ng-template>
            <div class="ai-line mb-3" *ngFor="let line of resolvedLines">
              <div class="font-bold">{{ line.extracted.description }} — {{ line.extracted.qty }} {{ line.extracted.unit }}</div>
              <div *ngIf="line.extracted.partNumber" class="text-500">{{ line.extracted.partNumber }}</div>

              <div *ngIf="line.loading" class="text-500">Поиск в каталоге...</div>

              <div *ngIf="!line.loading && line.candidates.length === 0" class="text-500">Совпадений в каталоге не найдено.</div>

              <div *ngIf="!line.loading && line.candidates.length > 0" class="flex flex-column gap-1">
                <div *ngFor="let c of line.candidates" class="flex align-items-center gap-2">
                  <p-radioButton name="cand-{{line.extracted.description}}" [value]="c" [ngModel]="line.accepted"
                                 [disabled]="isKeyAccount && !newDocument"
                                 (onClick)="acceptCandidate(line, c)"></p-radioButton>
                  <span>{{ c.PRODCODE }} — {{ c.PRODNAME }}</span>
                  <p-tag [value]="(c.confidence * 100 | number:'1.0-0') + '%'"
                         [severity]="c.confidence >= 0.9 ? 'success' : 'warning'"></p-tag>
                </div>
                <p-button label="Не выбирать" styleClass="p-button-text p-button-sm w-max"
                          [disabled]="isKeyAccount && !newDocument"
                          (onClick)="skipCandidate(line)" *ngIf="line.accepted"></p-button>
              </div>
            </div>
            <small class="text-500">
              Принятые позиции попадают в таблицу «Товары» ниже — там можно поправить количество и цену
              или добавить товар, которого не было в письме.
            </small>
          </p-accordionTab>

          <!-- The same capture panel the standard flow gets, in the same place. The block
               above is the AI's *proposal*; this is the document's actual content, so a
               manager can correct a quantity, fix a price, or add something the email
               never mentioned without leaving the dialog. Shown for COPILOT-mode customers
               too (2026-09-15) once a document exists (from the standard tab) -- already
               correctly gated on `newDocument` alone; the extra `!isKeyAccount` exclusion
               was removed rather than the `newDocument` check changed. -->
          <p-accordionTab [selected]="true" *ngIf="newDocument">
            <ng-template pTemplate="header"><h5>Товары ({{ newDocument.items?.length ?? 0 }})</h5></ng-template>
            <app-new-document-dlg-doc-items appStopAccordionHomeEnd
              [newDocument]="newDocument"
              [allowAdd]="true"
              [showStock]="false"
              [showReserve]="false"
              [showSold]="false"
              [showShipped]="false"
              [showDiscount]="false"
              [showU4etka]="false"
              [showNote]="false"
              [showVprice]="false"
              [showDepartments]="false">
            </app-new-document-dlg-doc-items>
          </p-accordionTab>

        </p-accordion>
      </p-fieldset>
    </div>

    <div class="mt-4 pt-3 border-top-1 border-300">
      <p-button label="Отклонить заявку" icon="pi pi-times" styleClass="p-button-danger p-button-text"
                (onClick)="showAbandonInput = !showAbandonInput" *ngIf="!showAbandonInput"></p-button>

      <div *ngIf="showAbandonInput" class="flex flex-column gap-2">
        <label class="font-bold">Причина отклонения</label>
        <textarea pInputTextarea [(ngModel)]="abandonReason" rows="2" class="w-full"></textarea>
        <div>
          <p-button label="Подтвердить отклонение" styleClass="p-button-danger" icon="pi pi-times"
                    (onClick)="onAbandon()" [disabled]="!abandonReason.trim() || abandoning"></p-button>
          <p-button label="Отмена" styleClass="p-button-text ml-2" (onClick)="showAbandonInput = false"></p-button>
        </div>
      </div>
    </div>

  </div>
</div>
